import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Nfc, Radio, Fingerprint, ScanLine, ShieldCheck, MapPin, IdCard, Users, Boxes,
  DoorOpen, IndianRupee, Loader2, X, Plus, Trash2, ExternalLink, CheckCircle2,
  AlertTriangle, ArrowRight, Copy, PenLine, Info, PlayCircle, PauseCircle,
  Calendar, Lock, Split, ShoppingBag,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import useWebNfc from "@/hooks/useWebNfc";

// -----------------------------------------------------------------------------
// NFC / RFID Hub — one screen for every enterprise mode we support.
//
// Tabs: Tags · Login · Verify · Track · Access · Pay · Contact-swap · Cards
// The reader panel at the top drives every tab — one tap fills the "uid" input
// used by whichever tab is active.
// -----------------------------------------------------------------------------
const TABS = [
  { k: "tags",     l: "My tags",     I: Nfc },
  { k: "cards",    l: "Tap-cards",   I: IdCard },
  { k: "events",   l: "Event badges",I: Calendar },
  { k: "verify",   l: "Verify",      I: ShieldCheck },
  { k: "track",    l: "Track",       I: MapPin },
  { k: "access",   l: "Access",      I: DoorOpen },
  { k: "pay",      l: "Pay",         I: IndianRupee },
  { k: "split",    l: "Split-pay",   I: Split },
  { k: "swap",     l: "Contact swap",I: Users },
  { k: "inventory",l: "Inventory",   I: Boxes },
  { k: "kit",      l: "Buy kit",     I: ShoppingBag },
];

export default function NfcHub() {
  const { user } = useAuth();
  const [tab, setTab] = useState("tags");
  const [uid, setUid] = useState("");
  const nfc = useWebNfc();

  useSeo({
    title: "NFC · RFID Hub · Tap to login, verify, track, pay · find2near",
    description: "One dashboard for NFC and RFID: instant login, product authenticity, physical tracking, contactless payments, event badges and tap-cards.",
    canonical: "/nfc",
  });

  // When a tap is detected, dispatch to the right tab automatically.
  useEffect(() => {
    if (!nfc.lastRecord?.serialNumber) return;
    setUid(nfc.lastRecord.serialNumber);
    api.get("/nfc/dispatch", { params: { uid: nfc.lastRecord.serialNumber } }).then(r => {
      const k = r.data.kind;
      if (k === "card" && r.data.slug) window.location.href = `/c/${r.data.slug}`;
      else if (k === "verify") setTab("verify");
      else if (k === "track")  setTab("track");
      else if (k === "login")  setTab("tags");
    }).catch(() => {});
  }, [nfc.lastRecord]);

  if (!user) return (
    <div className="max-w-md mx-auto text-center py-24 px-6">
      <Nfc className="mx-auto text-emerald-500" size={44}/>
      <h2 className="heading text-2xl font-black mt-4">Sign in to manage NFC & RFID</h2>
      <p className="text-stone-500 mt-2 text-sm">Public tap-card landings still work without an account.</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6" data-testid="nfc-hub">
      <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Nfc size={12} className="text-emerald-600"/> NFC · RFID Hub</div>
      <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Tap. <span className="text-emerald-700">And you're in.</span></h1>
      <p className="text-stone-500 text-sm mt-1 max-w-xl">Instant login · product authenticity · physical tracking · contactless payments · event badges — all on one tag.</p>

      <ReaderPanel nfc={nfc} uid={uid} setUid={setUid}/>

      <div className="flex gap-1.5 mt-4 mb-3 flex-wrap">
        {TABS.map(({ k, l, I }) => (
          <button key={k} onClick={() => setTab(k)}
            className={`text-xs font-black rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 border ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
            data-testid={`nfc-tab-${k}`}>
            <I size={12}/> {l}
          </button>
        ))}
      </div>

      {tab === "tags"     && <TagsTab uid={uid} setUid={setUid}/>}
      {tab === "cards"    && <CardsTab/>}
      {tab === "events"   && <EventsTab/>}
      {tab === "verify"   && <VerifyTab uid={uid} setUid={setUid}/>}
      {tab === "track"    && <TrackTab uid={uid} setUid={setUid}/>}
      {tab === "access"   && <AccessTab uid={uid} setUid={setUid}/>}
      {tab === "pay"      && <PayTab uid={uid} setUid={setUid}/>}
      {tab === "split"    && <SplitTab/>}
      {tab === "swap"     && <SwapTab uid={uid} setUid={setUid}/>}
      {tab === "inventory"&& <InventoryTab uid={uid} setUid={setUid}/>}
      {tab === "kit"      && <KitTab/>}
    </div>
  );
}


// ── READER PANEL --------------------------------------------------------------
function ReaderPanel({ nfc, uid, setUid }) {
  return (
    <div className="mt-4 rounded-3xl bg-gradient-to-br from-stone-900 to-stone-950 text-white p-4" data-testid="nfc-reader">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${nfc.scanning ? "bg-emerald-500 animate-pulse" : "bg-white/10"}`}>
          <Radio size={22} className="text-white"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-black">Web NFC reader</div>
          <div className="text-sm">
            {!nfc.supported ? (
              <span className="text-amber-300">Web NFC unavailable — open this page in <b>Chrome for Android</b> over HTTPS, or type a UID manually below.</span>
            ) : nfc.scanning ? (
              <>Hold your phone near the tag… <span className="text-emerald-300 font-black">listening</span></>
            ) : (
              <>Tap "Start" and hover your phone over an NFC tag to read/write.</>
            )}
          </div>
          {nfc.error && <div className="text-[11px] text-red-300 mt-1">{nfc.error}</div>}
          {nfc.lastRecord?.serialNumber && (
            <div className="text-[11px] text-emerald-300 mt-1 font-mono truncate">
              Last read: {nfc.lastRecord.serialNumber}{nfc.lastRecord.url && ` · ${nfc.lastRecord.url}`}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {nfc.scanning ? (
            <Button size="sm" onClick={nfc.stopScan} className="rounded-full bg-white/10 hover:bg-white/20 font-black" data-testid="nfc-stop">
              <PauseCircle size={12} className="mr-1"/> Stop
            </Button>
          ) : (
            <Button size="sm" onClick={nfc.startScan} disabled={!nfc.supported} className="rounded-full bg-emerald-500 hover:bg-emerald-400 font-black" data-testid="nfc-start">
              <PlayCircle size={12} className="mr-1"/> Start
            </Button>
          )}
        </div>
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={uid} onChange={e => setUid(e.target.value)} placeholder="Or paste / type a tag UID here" className="rounded-full text-sm bg-white/5 border-white/10 text-white placeholder:text-white/40" data-testid="nfc-uid-input"/>
        {uid && <Button onClick={() => { navigator.clipboard?.writeText(uid); toast.success("UID copied"); }} className="rounded-full bg-white/10 hover:bg-white/20" size="sm"><Copy size={12}/></Button>}
      </div>
    </div>
  );
}


// ── TAGS ---------------------------------------------------------------------
function TagsTab({ uid, setUid }) {
  const [tags, setTags]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [label, setLabel] = useState("");
  const [pin, setPin]     = useState("");
  const [kind, setKind]   = useState("login");

  const load = () => api.get("/nfc/tags").then(r => setTags(r.data));
  useEffect(() => { load(); }, []);

  const link = async () => {
    if (!uid.trim()) { toast.error("Read or type a tag UID first"); return; }
    setBusy(true);
    try {
      await api.post("/nfc/tags", { uid: uid.trim(), label: label.trim() || undefined, kind, pin: pin.trim() || undefined });
      toast.success("Tag linked");
      setLabel(""); setPin("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Revoke tag "${t.label || t.tag_id}"?`)) return;
    await api.delete(`/nfc/tags/${t.tag_id}`);
    load();
  };

  const togglePublic = async (t) => {
    await api.patch(`/nfc/tags/${t.tag_id}/track-public`, null, { params: { public: !t.public_track } });
    load();
  };

  return (
    <div className="space-y-3" data-testid="nfc-tags-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Link a new tag</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input placeholder="Label (e.g. 'Office keycard')" value={label} onChange={e => setLabel(e.target.value)} className="rounded-full text-sm" data-testid="tag-label"/>
          <select value={kind} onChange={e => setKind(e.target.value)} className="rounded-full text-sm border border-stone-200 px-3 py-1.5 bg-white" data-testid="tag-kind">
            {["login", "verify", "track", "card", "multi"].map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <Input placeholder="Optional PIN (4-8 digits)" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} inputMode="numeric" className="rounded-full text-sm" data-testid="tag-pin"/>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={link} disabled={busy || !uid.trim()} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-9" data-testid="tag-link">
            {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <Plus size={12} className="mr-1"/>} Link tag
          </Button>
          <div className="text-[11px] text-stone-500">UID: <span className="font-mono">{uid || "—"}</span></div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
        <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">{tags.length} linked tags</div>
        <div className="divide-y divide-stone-100">
          {tags.length === 0 && <div className="p-6 text-sm text-stone-500 text-center">No tags yet — link your first one above.</div>}
          {tags.map(t => (
            <div key={t.tag_id} className="p-3 flex items-center gap-2" data-testid={`tag-${t.tag_id}`}>
              <div className="w-9 h-9 rounded-2xl bg-emerald-50 grid place-items-center shrink-0"><Nfc size={16} className="text-emerald-600"/></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{t.label || t.tag_id}</div>
                <div className="text-[11px] text-stone-500 flex flex-wrap items-center gap-1.5">
                  <span className="uppercase font-black tracking-widest">{t.kind}</span>
                  {t.pin_hash && <span className="text-amber-700">🔒 PIN-protected</span>}
                  {t.product_code && <span>📦 {t.product_code}</span>}
                  {t.card_slug   && <span>🏷 /c/{t.card_slug}</span>}
                  {t.login_count && <span>· {t.login_count} logins</span>}
                  {t.checkpoint_count && <span>· {t.checkpoint_count} scans</span>}
                </div>
              </div>
              <button onClick={() => togglePublic(t)} className={`text-[10px] font-black uppercase rounded-full px-2 py-0.5 ${t.public_track ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`} title="Toggle public track">
                {t.public_track ? "public" : "private"}
              </button>
              <button onClick={() => { setUid(t.tag_id); toast.info("Loaded — switch to a tab to use"); }} className="text-stone-400 hover:text-emerald-700" title="Use in a tab">
                <ArrowRight size={13}/>
              </button>
              <button onClick={() => remove(t)} className="text-stone-400 hover:text-red-600" data-testid={`tag-del-${t.tag_id}`}><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── VERIFY -------------------------------------------------------------------
function VerifyTab({ uid, setUid }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    if (!uid.trim()) { toast.error("Read or type a UID first"); return; }
    setBusy(true);
    try {
      const r = await api.get("/nfc/verify", { params: { uid: uid.trim() } });
      setResult(r.data);
    } catch { toast.error("Verify failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-verify-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-2">
        <Button onClick={verify} disabled={busy || !uid.trim()} className="rounded-full bg-teal-600 hover:bg-teal-700 font-black h-9" data-testid="verify-btn">
          {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <ShieldCheck size={12} className="mr-1"/>} Verify authenticity
        </Button>
        <div className="text-[11px] text-stone-500">Scans a tag against our tag ledger + ERP product record.</div>
      </div>

      {result && (
        <div className={`rounded-3xl border p-4 ${result.is_authentic ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`} data-testid="verify-result">
          <div className="flex items-center gap-2 mb-2">
            {result.is_authentic ? <CheckCircle2 className="text-emerald-700" size={22}/> : <AlertTriangle className="text-rose-700" size={22}/>}
            <div className="font-black text-lg">{result.is_authentic ? "Authentic ✓" : "Not verified"}</div>
            {result.kind && <span className="text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 bg-white border border-stone-200 ml-auto">{result.kind}</span>}
          </div>
          {result.product && (
            <div className="space-y-1 text-sm">
              <div><b>Product:</b> {result.product.item_code} — qty {result.product.quantity} {result.product.unit || ""}</div>
              {result.product.cost_per_unit && <div><b>Cost / unit:</b> ₹{result.product.cost_per_unit}</div>}
              {result.seller?.name && <div><b>Seller:</b> {result.seller.name}</div>}
              <div className="text-xs text-stone-500">First seen: {(result.first_seen || "").slice(0, 10)} · Verified {result.verify_count} times</div>
            </div>
          )}
          {!result.product && <div className="text-xs text-stone-600">{result.reason ? `Reason: ${result.reason.replace(/_/g, " ")}` : "This tag is registered but not bound to a product."}</div>}
        </div>
      )}
    </div>
  );
}


// ── TRACK --------------------------------------------------------------------
function TrackTab({ uid, setUid }) {
  const [scans, setScans] = useState([]);
  const [busy, setBusy]   = useState(false);
  const [note, setNote]   = useState("");
  const [label, setLabel] = useState("");

  const load = async () => {
    if (!uid.trim()) return;
    try {
      const r = await api.get(`/nfc/track/${encodeURIComponent(uid.trim())}`);
      setScans(r.data.scans || []);
    } catch (e) { setScans([]); toast.error(e.response?.data?.detail || "Track view unavailable"); }
  };

  const checkpoint = async () => {
    setBusy(true);
    try {
      const c = await new Promise(res =>
        navigator.geolocation
          ? navigator.geolocation.getCurrentPosition(p => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({}), { timeout: 3000 })
          : res({})
      );
      await api.post("/nfc/checkpoint", { uid: uid.trim(), ...c, location_label: label.trim() || undefined, note: note.trim() || undefined });
      toast.success("Checkpoint scan logged");
      setNote(""); setLabel("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-track-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Log a checkpoint scan</div>
        <Input placeholder="Location label (e.g. 'Delhi hub')" value={label} onChange={e => setLabel(e.target.value)} className="rounded-full text-sm" data-testid="track-label"/>
        <Input placeholder="Note" value={note} onChange={e => setNote(e.target.value)} className="rounded-full text-sm" data-testid="track-note"/>
        <div className="flex gap-2">
          <Button onClick={checkpoint} disabled={busy || !uid.trim()} className="rounded-full bg-blue-600 hover:bg-blue-700 font-black h-9" data-testid="track-scan">
            {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <ScanLine size={12} className="mr-1"/>} Log scan
          </Button>
          <Button variant="outline" onClick={load} className="rounded-full h-9" data-testid="track-load">Load timeline</Button>
        </div>
      </div>

      {scans.length > 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="track-timeline">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">{scans.length} checkpoint scans</div>
          <div className="divide-y divide-stone-100">
            {scans.map(s => (
              <div key={s.scan_id} className="p-3 flex items-start gap-2">
                <MapPin size={13} className="text-blue-600 mt-0.5"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">{s.location_label || "Unnamed spot"}</div>
                  <div className="text-[11px] text-stone-500">{s.scanner_name || "unknown"} · {(s.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  {s.note && <div className="text-[11px] text-stone-600">{s.note}</div>}
                </div>
                {s.lat && s.lng && <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline">Map <ExternalLink size={10} className="inline"/></a>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── ACCESS -------------------------------------------------------------------
function AccessTab({ uid }) {
  const [doors, setDoors] = useState([]);
  const [doorId, setDoorId] = useState("");
  const [label, setLabel]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [check, setCheck]   = useState(null);

  const load = () => api.get("/nfc/access/doors").then(r => setDoors(r.data));
  useEffect(() => { load(); }, []);

  const addDoor = async () => {
    if (!doorId.trim()) return;
    setBusy(true);
    try { await api.post("/nfc/access/doors", { door_id: doorId.trim(), label: label.trim() || undefined, allowed_tag_ids: [] });
          toast.success("Door added"); setDoorId(""); setLabel(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const testAccess = async (d) => {
    if (!uid.trim()) { toast.error("Tap a tag first"); return; }
    try {
      const r = await api.post("/nfc/access/check", null, { params: { uid: uid.trim(), door_id: d.door_id } });
      setCheck({ door: d.label, granted: r.data.granted });
    } catch (e) { setCheck({ door: d.label, granted: false, reason: e.response?.data?.detail }); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-access-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Add a door / gate</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input placeholder="Door ID (e.g. main-gate)" value={doorId} onChange={e => setDoorId(e.target.value)} className="rounded-full text-sm" data-testid="door-id"/>
          <Input placeholder="Label"                    value={label}  onChange={e => setLabel(e.target.value)}  className="rounded-full text-sm" data-testid="door-label"/>
        </div>
        <Button onClick={addDoor} disabled={busy} className="rounded-full bg-purple-600 hover:bg-purple-700 font-black h-9" data-testid="door-add"><Plus size={12} className="mr-1"/> Add door</Button>
      </div>

      <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
        <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">{doors.length} doors</div>
        <div className="divide-y divide-stone-100">
          {doors.map(d => (
            <div key={d.door_id} className="p-3 flex items-center gap-2" data-testid={`door-${d.door_id}`}>
              <DoorOpen size={16} className="text-purple-600"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{d.label}</div>
                <div className="text-[11px] text-stone-500 font-mono">{d.door_id} · {(d.allowed_tag_ids || []).length} allowed tags</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => testAccess(d)} className="rounded-full h-7 text-xs" data-testid={`door-test-${d.door_id}`}>Test tap</Button>
            </div>
          ))}
        </div>
      </div>

      {check && (
        <div className={`rounded-2xl p-3 text-sm font-black ${check.granted ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`} data-testid="access-check-result">
          {check.granted ? "✓ Granted" : "✗ Denied"} — {check.door}{check.reason ? ` (${check.reason})` : ""}
        </div>
      )}
    </div>
  );
}


// ── PAY ----------------------------------------------------------------------
function PayTab({ uid, setUid }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/nfc/pay/history").then(r => setHistory(r.data));
  useEffect(() => { load(); }, []);

  const pay = async () => {
    const paise = Math.round(Number(amount) * 100);
    if (!paise || paise < 1) { toast.error("Enter a positive amount"); return; }
    setBusy(true);
    try {
      await api.post("/nfc/pay", { payee_uid: uid.trim(), amount_paise: paise, note: note.trim() || undefined });
      toast.success(`Paid ₹${amount}`);
      setAmount(""); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-pay-tab">
      <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-800 text-white p-4">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Contactless payment</div>
        <div className="text-sm opacity-80 mb-2">Tap the merchant's terminal tag, then enter the amount.</div>
        <div className="flex flex-wrap gap-2">
          <Input inputMode="decimal" placeholder="Amount ₹" value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-full text-sm bg-white/10 border-white/20 text-white placeholder:text-white/60 w-36" data-testid="pay-amount"/>
          <Input placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} className="rounded-full text-sm bg-white/10 border-white/20 text-white placeholder:text-white/60 flex-1 min-w-[140px]" data-testid="pay-note"/>
          <Button onClick={pay} disabled={busy || !uid.trim() || !amount} className="rounded-full bg-white text-emerald-800 hover:bg-emerald-50 font-black h-9" data-testid="pay-btn">
            {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <IndianRupee size={12} className="mr-1"/>} Pay
          </Button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="pay-history">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">History</div>
          <div className="divide-y divide-stone-100">
            {history.map(h => (
              <div key={h.txn_id} className="p-3 flex items-center gap-2 text-sm">
                <IndianRupee size={13} className="text-emerald-600"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">₹{(h.amount_paise / 100).toFixed(2)}</div>
                  <div className="text-[11px] text-stone-500">{(h.created_at || "").slice(0, 16).replace("T", " ")}{h.note && ` · ${h.note}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── CONTACT SWAP -------------------------------------------------------------
function SwapTab({ uid, setUid }) {
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/nfc/contacts/mine").then(r => setContacts(r.data));
  useEffect(() => { load(); }, []);

  const swap = async () => {
    if (!uid.trim()) { toast.error("Tap another user's badge first"); return; }
    setBusy(true);
    try { await api.post("/nfc/contact-swap", { other_uid: uid.trim() }); toast.success("Contact saved 🤝"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Swap failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-swap-tab">
      <div className="rounded-3xl bg-gradient-to-br from-pink-600 to-fuchsia-700 text-white p-4">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Event badge · contact swap</div>
        <div className="text-sm opacity-80 mb-2">Two attendees tap each other's badge — both save the contact instantly.</div>
        <Button onClick={swap} disabled={busy || !uid.trim()} className="rounded-full bg-white text-pink-800 hover:bg-pink-50 font-black h-9" data-testid="swap-btn">
          {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <Users size={12} className="mr-1"/>} Save this contact
        </Button>
      </div>

      <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="swap-list">
        <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">{contacts.length} contacts</div>
        <div className="divide-y divide-stone-100">
          {contacts.map(c => (
            <div key={c.other_user_id} className="p-3 flex items-center gap-2">
              {c.profile?.picture ? <img src={c.profile.picture} alt="" className="w-9 h-9 rounded-full object-cover"/> : <div className="w-9 h-9 rounded-full bg-stone-100 grid place-items-center"><Users size={14} className="text-stone-400"/></div>}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{c.profile?.name || "Unknown"}{c.card?.title && <span className="text-stone-500 font-normal"> · {c.card.title}</span>}</div>
                <div className="text-[11px] text-stone-500 truncate">{c.profile?.email}</div>
              </div>
              {c.card?.slug && <a href={`/c/${c.card.slug}`} className="text-xs font-bold text-fuchsia-700 hover:underline">View card <ExternalLink size={9} className="inline"/></a>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── INVENTORY ---------------------------------------------------------------
function InventoryTab({ uid, setUid }) {
  const [busy, setBusy] = useState(false);
  const [productCode, setProductCode] = useState("");
  const [delta, setDelta] = useState(-1);
  const [events, setEvents] = useState([]);

  const bind = async () => {
    if (!uid.trim() || !productCode.trim()) return;
    // requires a tag_id — the first tag matching the UID
    setBusy(true);
    try {
      const my = (await api.get("/nfc/tags")).data;
      const t  = my.find(x => x.tag_id === uid.trim());   // matches on tag_id explicitly
      if (!t) { toast.error("Link this tag first in the Tags tab"); setBusy(false); return; }
      await api.post("/nfc/bind-product", { tag_id: t.tag_id, product_code: productCode.trim() });
      toast.success("Bound to product");
    } catch (e) { toast.error(e.response?.data?.detail || "Bind failed"); }
    finally { setBusy(false); }
  };

  const scan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/nfc/inventory/scan", { uid: uid.trim(), delta });
      toast.success(`Stock updated to ${r.data.new_qty}`);
      setProductCode(r.data.product_code);
      const ev = await api.get(`/nfc/inventory/${r.data.product_code}/events`);
      setEvents(ev.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-inventory-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Bind & scan</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input placeholder="Product code (from ERP)" value={productCode} onChange={e => setProductCode(e.target.value)} className="rounded-full text-sm" data-testid="inv-product"/>
          <select value={delta} onChange={e => setDelta(Number(e.target.value))} className="rounded-full text-sm border border-stone-200 px-3 py-1.5 bg-white">
            <option value={-1}>-1 (Dispatch)</option>
            <option value={+1}>+1 (Restock)</option>
            <option value={-10}>-10</option>
            <option value={+10}>+10</option>
          </select>
          <div className="flex gap-1">
            <Button onClick={bind} disabled={busy || !uid.trim() || !productCode.trim()} className="rounded-full bg-stone-700 hover:bg-stone-900 font-black h-9 flex-1" data-testid="inv-bind"><PenLine size={12} className="mr-1"/> Bind</Button>
            <Button onClick={scan} disabled={busy || !uid.trim()} className="rounded-full bg-amber-600 hover:bg-amber-700 font-black h-9 flex-1" data-testid="inv-scan"><Boxes size={12} className="mr-1"/> Scan</Button>
          </div>
        </div>
      </div>

      {events.length > 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="inv-events">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">Movement history</div>
          <div className="divide-y divide-stone-100">
            {events.map(e => (
              <div key={e.event_id} className="p-2 flex items-center gap-2 text-xs">
                <Boxes size={12} className="text-amber-600"/>
                <span className={`font-black ${e.delta > 0 ? "text-emerald-700" : "text-rose-700"}`}>{e.delta > 0 ? "+" : ""}{e.delta}</span>
                <span className="text-stone-500">→ qty {e.new_qty}</span>
                <span className="ml-auto text-stone-400 font-mono">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── TAP-CARDS ---------------------------------------------------------------
function CardsTab() {
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", company: "", email: "", phone: "", website: "", linkedin: "", bio: "" });
  const navigate = useNavigate();

  const load = () => api.get("/nfc/cards/mine").then(r => setCards(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/nfc/cards", form);
      toast.success("Card created");
      setForm({ name: "", title: "", company: "", email: "", phone: "", website: "", linkedin: "", bio: "" });
      load();
      navigate(`/c/${r.data.slug}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-cards-tab">
      <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Create tap-card</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-full text-sm" data-testid="card-name"/>
          <Input placeholder="Title (Head of Design)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-full text-sm" data-testid="card-title"/>
          <Input placeholder="Company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="rounded-full text-sm" data-testid="card-company"/>
          <Input placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-full text-sm" data-testid="card-email"/>
          <Input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-full text-sm" data-testid="card-phone"/>
          <Input placeholder="Website" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className="rounded-full text-sm" data-testid="card-website"/>
          <Input placeholder="LinkedIn URL" value={form.linkedin} onChange={e => setForm({ ...form, linkedin: e.target.value })} className="rounded-full text-sm sm:col-span-2" data-testid="card-linkedin"/>
        </div>
        <Textarea rows={2} placeholder="Short bio (max 280 chars)" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} maxLength={280} className="rounded-2xl text-sm" data-testid="card-bio"/>
        <Button onClick={create} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-9" data-testid="card-create"><Plus size={12} className="mr-1"/> Create tap-card</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        {cards.map(c => (
          <a key={c.card_id} href={`/c/${c.slug}`} className="rounded-3xl bg-white border border-stone-200 hover:border-emerald-400 hover:shadow-md p-3 transition-all" data-testid={`card-${c.slug}`}>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 grid place-items-center"><IdCard size={18} className="text-emerald-600"/></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{c.name}</div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 truncate">{c.title || "—"}{c.company && ` · ${c.company}`}</div>
              </div>
              <ExternalLink size={12} className="text-stone-400"/>
            </div>
            <div className="mt-1 text-[11px] text-stone-500 font-mono truncate">/c/{c.slug} · {c.views} views</div>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── EVENT BADGES ------------------------------------------------------------
//   Bulk-create tags bound to a conference event; each badge tap reveals the
//   schedule and speaker bios on a public /e/:slug landing.
function EventsTab() {
  const [events, setEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showBadges, setShowBadges] = useState(null);   // event doc

  const load = () => api.get("/nfc/events/mine").then(r => setEvents(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="nfc-events-tab">
      <div className="rounded-3xl bg-gradient-to-br from-fuchsia-600 to-purple-800 text-white p-4 flex items-start gap-3">
        <Calendar size={18} className="mt-0.5"/>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Conference organiser mode</div>
          <div className="text-sm opacity-90 mb-2">Program hundreds of badges in one go — each tap opens the event schedule + speaker bios.</div>
          <Button onClick={() => setShowCreate(true)} className="rounded-full bg-white text-purple-800 hover:bg-purple-50 font-black h-9" data-testid="event-create-btn"><Plus size={12} className="mr-1"/> New event</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2" data-testid="events-list">
        {events.map(e => (
          <div key={e.event_id} className="rounded-3xl bg-white border border-stone-200 p-3" data-testid={`event-${e.event_id}`}>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-2xl bg-fuchsia-50 grid place-items-center"><Calendar size={16} className="text-fuchsia-600"/></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{e.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold truncate">{e.venue}{e.city && ` · ${e.city}`}</div>
              </div>
            </div>
            <div className="mt-1 text-[11px] text-stone-500">
              {e.starts_on} → {e.ends_on} · {e.speakers?.length || 0} speakers · {e.sessions?.length || 0} sessions · {e.badges_issued || 0} badges
            </div>
            <div className="mt-2 flex gap-1 flex-wrap">
              <a href={`/e/${e.slug}`} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-fuchsia-700 hover:underline">Public page <ExternalLink size={9} className="inline"/></a>
              <button onClick={() => setShowBadges(e)} className="text-[11px] font-bold text-stone-700 hover:underline ml-2" data-testid={`event-badges-btn-${e.event_id}`}>Program badges</button>
            </div>
          </div>
        ))}
      </div>

      {showCreate && <EventCreateModal onClose={saved => { setShowCreate(false); if (saved) load(); }}/>}
      {showBadges && <BulkBadgeModal event={showBadges} onClose={saved => { setShowBadges(null); if (saved) load(); }}/>}
    </div>
  );
}

function EventCreateModal({ onClose }) {
  const [form, setForm] = useState({ name: "", starts_on: "", ends_on: "", venue: "", city: "", website: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim() || !form.starts_on || !form.ends_on) { toast.error("Name and dates required"); return; }
    setBusy(true);
    try { await api.post("/nfc/events", { ...form, speakers: [], sessions: [] }); toast.success("Event created"); onClose(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3" data-testid="event-create-modal">
        <div className="flex items-center justify-between"><h3 className="font-black text-stone-900">Create event</h3><button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button></div>
        <Input placeholder="Event name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-full text-sm" data-testid="event-name"/>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={form.starts_on} onChange={e => setForm({ ...form, starts_on: e.target.value })} className="rounded-full text-sm" data-testid="event-starts"/>
          <Input type="date" value={form.ends_on}   onChange={e => setForm({ ...form, ends_on: e.target.value })}   className="rounded-full text-sm" data-testid="event-ends"/>
        </div>
        <Input placeholder="Venue" value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })} className="rounded-full text-sm"/>
        <Input placeholder="City"  value={form.city}  onChange={e => setForm({ ...form, city: e.target.value })}  className="rounded-full text-sm"/>
        <Input placeholder="Event website" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className="rounded-full text-sm"/>
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black h-9" data-testid="event-save">
          {busy ? <Loader2 size={13} className="animate-spin mr-1"/> : <Plus size={13} className="mr-1"/>} Create event
        </Button>
      </div>
    </div>
  );
}

function BulkBadgeModal({ event, onClose }) {
  const [uids, setUids] = useState("");
  const [labelPrefix, setLabelPrefix] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const list = uids.split(/[\n,;\s]+/).map(x => x.trim()).filter(Boolean);
    if (!list.length) { toast.error("Paste at least one UID"); return; }
    setBusy(true);
    try {
      const r = await api.post("/nfc/events/badges", { event_id: event.event_id, uids: list, label_prefix: labelPrefix.trim() || undefined });
      setResult(r.data);
      toast.success(`Programmed ${r.data.created} badges`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(true)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-lg space-y-3" data-testid="bulk-badge-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">Bulk-program badges · {event.name}</h3>
          <button onClick={() => onClose(true)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <div className="text-xs text-stone-500">Paste one NFC UID per line (up to 500). Duplicates are skipped automatically.</div>
        <Textarea rows={7} placeholder="04:A3:B2:C1&#10;04:A3:B2:C2&#10;04:A3:B2:C3" value={uids} onChange={e => setUids(e.target.value)} className="rounded-2xl text-sm font-mono" data-testid="bulk-badge-uids"/>
        <Input placeholder="Label prefix (defaults to event name)" value={labelPrefix} onChange={e => setLabelPrefix(e.target.value)} className="rounded-full text-sm" data-testid="bulk-badge-label"/>
        <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black h-9" data-testid="bulk-badge-submit">
          {busy ? <Loader2 size={13} className="animate-spin mr-1"/> : <Nfc size={13} className="mr-1"/>} Program badges
        </Button>
        {result && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900" data-testid="bulk-badge-result">
            Created <b>{result.created}</b> · Skipped <b>{result.skipped}</b> (already registered).
          </div>
        )}
      </div>
    </div>
  );
}


// ── SPLIT-THE-BILL TAP ------------------------------------------------------
function SplitTab() {
  const [uids, setUids] = useState([""]);   // 2+ payer UIDs
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/nfc/split-pay/history").then(r => setHistory(r.data));
  useEffect(() => { load(); }, []);

  const setUid = (i, v) => setUids(u => u.map((x, j) => j === i ? v : x));
  const addPayer = () => setUids(u => [...u, ""]);
  const rmPayer  = (i) => setUids(u => u.filter((_, j) => j !== i));

  const submit = async () => {
    const list = uids.map(x => x.trim()).filter(Boolean);
    if (list.length < 1) { toast.error("Need at least one payer tag"); return; }
    const paise = Math.round(Number(amount) * 100);
    if (!paise || paise < 1) { toast.error("Enter a positive total"); return; }
    setBusy(true);
    try {
      const r = await api.post("/nfc/split-pay", { amount_paise_total: paise, payer_uids: list, note: note.trim() || undefined });
      toast.success(`Split ₹${amount} between ${r.data.payers.length} payers`);
      setAmount(""); setNote(""); setUids([""]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Split failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="nfc-split-tab">
      <div className="rounded-3xl bg-gradient-to-br from-amber-500 to-orange-700 text-white p-4">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Split the bill · one tap each</div>
        <div className="text-sm opacity-90 mb-3">Each friend taps their NFC once. We split the amount equally and debit every wallet at the same moment.</div>
        <div className="space-y-2">
          {uids.map((u, i) => (
            <div key={i} className="flex gap-2" data-testid={`split-uid-row-${i}`}>
              <Input value={u} onChange={e => setUid(i, e.target.value)} placeholder={`Payer ${i + 1} tag UID`} className="rounded-full text-sm bg-white/10 border-white/20 text-white placeholder:text-white/60" data-testid={`split-uid-${i}`}/>
              {uids.length > 1 && <button onClick={() => rmPayer(i)} className="text-white/70 hover:text-white"><X size={14}/></button>}
            </div>
          ))}
          <button onClick={addPayer} className="text-[11px] font-black text-white/80 hover:text-white" data-testid="split-add-payer">+ Add another payer</button>
          <div className="grid grid-cols-2 gap-2">
            <Input inputMode="decimal" placeholder="Total ₹" value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-full text-sm bg-white/10 border-white/20 text-white placeholder:text-white/60" data-testid="split-amount"/>
            <Input placeholder="Note (dinner @ Fio)" value={note} onChange={e => setNote(e.target.value)} className="rounded-full text-sm bg-white/10 border-white/20 text-white placeholder:text-white/60" data-testid="split-note"/>
          </div>
          <Button onClick={submit} disabled={busy || !amount} className="rounded-full bg-white text-orange-800 hover:bg-orange-50 font-black h-9 w-full" data-testid="split-submit">
            {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <Split size={12} className="mr-1"/>} Split & charge
          </Button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="split-history">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">History</div>
          <div className="divide-y divide-stone-100">
            {history.map(h => (
              <div key={h.split_id} className="p-3 text-sm">
                <div className="font-bold">₹{(h.amount_paise_total / 100).toFixed(2)} · {h.payers.length} payers</div>
                <div className="text-[11px] text-stone-500">{(h.created_at || "").slice(0, 16).replace("T", " ")}{h.note && ` · ${h.note}`}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ── BUY KIT ----------------------------------------------------------------
function KitTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState("");   // "quick" | "cart" | ""

  useEffect(() => {
    api.get("/products/construction", { params: { category: "nfc_kit" } })
      .then(r => setItems(r.data)).finally(() => setLoading(false));
    api.get("/nfc/kit/starter-bundle").then(r => setBundle(r.data)).catch(() => {});
  }, []);

  const addToCart = async () => {
    setBusy("cart");
    try {
      await api.post("/nfc/kit/starter-bundle/add-to-cart");
      toast.success("Bundle added to your cart");
      navigate("/cart");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add to cart failed");
    } finally { setBusy(""); }
  };

  const quickBuy = async () => {
    setBusy("quick");
    try {
      const r = await api.post("/nfc/kit/starter-bundle/quick-checkout", {
        origin_url: window.location.origin,
        payment_mode: "stripe",
      });
      if (r.data?.checkout_url) {
        toast.success("Redirecting to secure checkout…");
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Checkout unavailable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick-buy failed");
    } finally { setBusy(""); }
  };

  const subs = {};
  items.forEach(i => { (subs[i.sub || "misc"] ||= []).push(i); });

  return (
    <div className="space-y-3" data-testid="nfc-kit-tab">
      {bundle && (
        <div className="rounded-3xl bg-gradient-to-br from-amber-500 via-orange-600 to-rose-700 text-white p-4 shadow-xl relative overflow-hidden" data-testid="kit-starter-bundle">
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10"/>
          <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-white/5"/>
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-widest font-black bg-white text-orange-800 rounded-full px-2 py-0.5">Launch offer</span>
              <span className="text-[10px] uppercase tracking-widest font-black opacity-70">{bundle.savings_pct}% off · first-time buyers</span>
            </div>
            <div className="text-2xl font-black leading-tight">{bundle.name}</div>
            <div className="text-sm opacity-90 max-w-md">{bundle.tagline}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {bundle.items.map(it => (
                <span key={it.sku} className="text-[11px] font-bold bg-white/15 rounded-full px-2 py-0.5" data-testid={`kit-bundle-item-${it.sku}`}>
                  {it.name}{it.mrp_inr ? ` · ₹${it.mrp_inr}` : ""}
                </span>
              ))}
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <div className="text-3xl font-black">₹{bundle.launch_inr}</div>
              <div className="line-through opacity-70">₹{bundle.original_inr}</div>
              <div className="text-xs bg-emerald-500 rounded-full px-2 py-0.5 font-black">Save ₹{bundle.savings_inr}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                onClick={quickBuy}
                disabled={busy === "quick"}
                className="rounded-full bg-white text-orange-800 hover:bg-orange-50 font-black h-9"
                data-testid="kit-bundle-buy"
              >
                {busy === "quick" ? <Loader2 size={12} className="animate-spin mr-1"/> : <ShoppingBag size={12} className="mr-1"/>}
                One-click buy · ₹{bundle.launch_inr}
              </Button>
              <Button
                onClick={addToCart}
                disabled={busy === "cart"}
                variant="outline"
                className="rounded-full bg-white/10 border-white/40 text-white hover:bg-white/20 font-black h-9"
                data-testid="kit-bundle-add-cart"
              >
                {busy === "cart" ? <Loader2 size={12} className="animate-spin mr-1"/> : <Plus size={12} className="mr-1"/>} Add to cart
              </Button>
              {bundle.shipping_free && <span className="text-[11px] font-bold self-center opacity-80">🚚 Free shipping</span>}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-teal-900 text-white p-4">
        <div className="text-[10px] uppercase tracking-widest font-black opacity-70">Physical NFC · RFID kit</div>
        <div className="text-lg font-black">Or pick individual items</div>
        <div className="text-sm opacity-90">10 tags · a personalised tap-card · stickers · wristbands · optional door lock. Free shipping across India on orders above ₹999.</div>
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-stone-400"/></div>
      ) : (
        Object.entries(subs).map(([sub, list]) => (
          <div key={sub} className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
            <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">{sub.replace(/_/g, " ")}</div>
            <div className="divide-y divide-stone-100">
              {list.map(it => (
                <div key={it.sku} className="p-3 flex items-center gap-2" data-testid={`kit-${it.sku}`}>
                  <div className="w-9 h-9 rounded-2xl bg-emerald-50 grid place-items-center shrink-0"><Nfc size={16} className="text-emerald-600"/></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-stone-900 truncate">{it.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">{Object.entries(it.specs || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-black text-stone-900">₹{it.buy_inr}</div>
                    <div className="text-[10px] text-stone-400">{it.unit}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <a href="/products?category=nfc_kit" className="block text-center text-xs font-bold text-emerald-700 hover:underline pt-2">Open full kit catalogue →</a>
    </div>
  );
}

